import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { fetchMe, fetchHistory, type HistoryEntry } from '../api/me';
import { formatDateUK } from '../lib/format';
import { londonToday, addDays } from '../lib/gameDate';
import type { Difficulty } from '../api/puzzles';

const DIFFICULTIES: Difficulty[] = ['EASY', 'MEDIUM', 'HARD'];
const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  EASY: 'Easy',
  MEDIUM: 'Medium',
  HARD: 'Hard',
};

function lastNDates(n: number): string[] {
  const out: string[] = [];
  let date = londonToday();
  for (let i = 0; i < n; i++) {
    out.unshift(date);
    date = addDays(date, -1);
  }
  return out;
}

const HEAT = ['bg-stone-100', 'bg-emerald-200', 'bg-emerald-400', 'bg-emerald-600'];

export default function History() {
  const { user, loading } = useAuth();

  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe, enabled: !!user });
  const days = useMemo(() => lastNDates(30), []);
  const historyQuery = useQuery({
    queryKey: ['me', 'history', 'overview'],
    queryFn: () => fetchHistory({ from: days[0], to: days[days.length - 1], page_size: 100 }),
    enabled: !!user,
  });

  // Per day: difficulties won and difficulties merely started, for the 30-day
  // heatmap (started-without-a-win renders amber so abandoned days stay visible).
  const heatByDate = useMemo(() => {
    const map = new Map<string, { won: Set<string>; played: Set<string> }>();
    for (const e of historyQuery.data?.entries ?? []) {
      const day = map.get(e.puzzle_date) ?? { won: new Set<string>(), played: new Set<string>() };
      day.played.add(e.difficulty);
      if (e.won) day.won.add(e.difficulty);
      map.set(e.puzzle_date, day);
    }
    return map;
  }, [historyQuery.data]);

  // The three most recent days that have any play, for the "recent plays" peek.
  const recentDays = useMemo(() => {
    const map = new Map<string, HistoryEntry[]>();
    for (const e of historyQuery.data?.entries ?? []) {
      const list = map.get(e.puzzle_date) ?? [];
      list.push(e);
      map.set(e.puzzle_date, list);
    }
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1)).slice(0, 3);
  }, [historyQuery.data]);

  if (!loading && !user) {
    return (
      <section data-testid="page-history">
        <h1 className="text-2xl font-bold">History</h1>
        <p className="mt-4 text-stone-600">
          <Link to="/login" className="font-semibold text-emerald-700 underline">
            Sign in
          </Link>{' '}
          to keep a history of your daily games.
        </p>
      </section>
    );
  }

  const streaks = meQuery.data?.streaks ?? [];

  return (
    <section data-testid="page-history">
      <h1 className="text-2xl font-bold">History</h1>
      {meQuery.data && !meQuery.data.user.email_verified ? (
        <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800" role="status">
          Your email isn't verified yet — check your inbox.
        </p>
      ) : null}

      <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-stone-500">Streaks</h2>
      <div className="mt-2 grid grid-cols-3 gap-3">
        {streaks.map((s) => (
          <div
            key={s.difficulty}
            data-testid={`streak-medallion-${s.difficulty.toLowerCase()}`}
            className="rounded-xl border border-stone-200 bg-white p-3 text-center"
          >
            <p className="text-xs font-medium text-stone-500">{DIFFICULTY_LABELS[s.difficulty]}</p>
            <p className="mt-1 text-3xl font-bold text-emerald-700">{s.current_streak}</p>
            <p className="text-xs text-stone-500">best {s.max_streak}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-stone-500">Totals</h2>
      <div className="mt-2 overflow-hidden rounded-xl border border-stone-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase text-stone-500">
            <tr>
              <th className="px-4 py-2">Difficulty</th>
              <th className="px-4 py-2">Wins</th>
              <th className="px-4 py-2">Played</th>
              <th className="px-4 py-2">Win rate</th>
            </tr>
          </thead>
          <tbody>
            {streaks.map((s) => (
              <tr key={s.difficulty} className="border-t border-stone-100">
                <td className="px-4 py-2 font-medium">{DIFFICULTY_LABELS[s.difficulty]}</td>
                <td className="px-4 py-2">{s.total_wins}</td>
                <td className="px-4 py-2">{s.total_played}</td>
                <td className="px-4 py-2">
                  {s.total_played > 0
                    ? `${Math.round((s.total_wins / s.total_played) * 100)}%`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-stone-500">
        Last 30 days
      </h2>
      <div data-testid="heatmap" className="mt-2 grid grid-cols-10 gap-1.5">
        {days.map((date) => {
          const day = heatByDate.get(date);
          const wins = day?.won.size ?? 0;
          const startedOnly = wins === 0 && (day?.played.size ?? 0) > 0;
          const label =
            wins > 0
              ? `${date}: ${wins} win${wins === 1 ? '' : 's'}`
              : startedOnly
                ? `${date}: played, no win`
                : `${date}: not played`;
          return (
            <div
              key={date}
              title={label}
              aria-label={label}
              role="img"
              className={`aspect-square rounded ${startedOnly ? 'bg-amber-200' : HEAT[Math.min(wins, 3)]}`}
            />
          );
        })}
      </div>
      <p className="mt-2 text-xs text-stone-500">
        <span className="mr-3 inline-flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded bg-emerald-400" /> wins
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded bg-amber-200" /> played, no win
        </span>
      </p>

      <div className="mt-6 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-500">
          Recent plays
        </h2>
        <Link
          to="/history/plays"
          className="text-sm font-semibold text-emerald-700 hover:underline"
        >
          View all plays <span aria-hidden="true">→</span>
        </Link>
      </div>
      {recentDays.length === 0 ? (
        <p className="mt-2 text-sm text-stone-600">
          Nothing yet — play{' '}
          <Link to="/" className="text-emerald-700 underline">
            today's puzzles
          </Link>
          .
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {recentDays.map(([date, entries]) => (
            <li
              key={date}
              className="flex items-center justify-between rounded-lg border border-stone-200 bg-white px-4 py-3"
            >
              <span className="font-semibold">{formatDateUK(date)}</span>
              <span className="flex flex-wrap items-center gap-2">
                {DIFFICULTIES.map((difficulty) => {
                  const own = entries.filter((e) => e.difficulty === difficulty);
                  if (own.length === 0) return null;
                  const won = own.some((e) => e.won);
                  const best = own.reduce((m, e) => Math.max(m, e.final_score), 0);
                  return (
                    <span
                      key={difficulty}
                      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        won ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-500'
                      }`}
                    >
                      {DIFFICULTY_LABELS[difficulty]} {won ? '✓' : '·'} {best}
                    </span>
                  );
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
